import { ViewOrientation, Color, DrawMode, OperatorId, Button, Point3, Operators } from "@ts3d-hoops/web-viewer";
import type { Box, GenericType, IWebViewer, NodeId, StringStringMap } from "@ts3d-hoops/web-viewer";

/** `MeasureMarkup._positions` is `protected`; this subclass republishes it as public (a legal
 *  widening) so `boundingMeasurement` can write the leader and text positions — indices 2-4 —
 *  directly. `declare` is required: a real field declaration would redefine `_positions` as
 *  `undefined` after the base constructor has filled it. `getClassName()` hard-references the
 *  base class's static `className`, so these markups still serialize as
 *  `MeasurePointPointDistanceMarkup`. */
class BoundingDistanceMarkup extends Operators.Markup.Measure.MeasurePointPointDistanceMarkup {
  declare _positions: Point3[];
}

export default class MeasurementAutomation {
  _viewer: IWebViewer;
  typeIdMap: Map<GenericType, Set<NodeId>>;

  constructor(hwv: IWebViewer) {
    this._viewer = hwv;
    this.typeIdMap = this._viewer.model.getGenericTypeIdMap();

    this.generateMeasurements();
  }

  async getExternalElements() {
    const externalElem: NodeId[] = [];
    const promises: Promise<void>[] = [];
    // Iterate over the map, updating each value
    for (const [key, value] of this.typeIdMap.entries()) {
      if (key === "IFCWALLSTANDARDCASE" || key === "IFCCOLUMN" || key === "IFCBUILDINGELEMENTPROXY") {
        // || key === "IFCCOLUMN" || key === "IFCWALL"
        for (const nodeId of value) {
          const p = new Promise<void>((resolve) => {
            this._viewer.model.getNodeProperties(nodeId).then((nodeProp: StringStringMap | null) => {
              if (nodeProp) {
                if (Object.hasOwn(nodeProp, "Text/WALL LOCATION") && nodeProp["Text/WALL LOCATION"] === "Exterior ") {
                  externalElem.push(nodeId);
                }
              }
              resolve();
            });
          });
          promises.push(p);
        }
      }
    }
    await Promise.all(promises);
    return externalElem;
  }

  async generateMeasurements() {
    // Overall measurements of entire unit.
    this._viewer.view.setViewOrientation(ViewOrientation.Top, 1000);
    this._viewer.view.setBackgroundColor(null, new Color(255, 255, 255));
    this._viewer.view.setDrawMode(DrawMode.HiddenLine);
    this._viewer.view.setAmbientOcclusionEnabled(false);
    this._viewer.view.getNavCube().disable();

    // this._viewer.operatorManager.set(Communicator.OperatorId.Pan, 0);
    const zoomOp = this._viewer.operatorManager.getOperator(OperatorId.Zoom);
    const panOp = this._viewer.operatorManager.getOperator(OperatorId.Pan);
    const orbitOp = this._viewer.operatorManager.getOperator(OperatorId.Orbit);
    panOp.setMapping(Button.Left);
    zoomOp.setMapping(Button.Middle);
    orbitOp.setPrimaryButton(Button.None);
    orbitOp.setMapping(Button.None);

    // `getModelBounding` takes (ignoreInvisible, includeExcluded); this call used to omit both,
    // so the engine received `undefined` for each and treated them as false. They are now passed
    // explicitly, which keeps the same bounding box without asserting the signature away.
    const bb = await this._viewer.model.getModelBounding(false, false);
    const bbCenter = Point3.add(bb.min, bb.max).scale(0.5);
    const bbDia = Point3.distance(bb.min, bb.max);
    this.boundingMeasurement(bb, 1, bbDia * 0.1, bbCenter, 1); // length
    this.boundingMeasurement(bb, 2, bbDia * 0.1, bbCenter, 1); // width

    // Measure spaces
    const spaces = this.typeIdMap.get("IFCSPACE");
    // KNOWN BUG, preserved verbatim: a model with no IFCSPACE entries makes this `undefined`
    // and the loop throws. Erased assertion; a guard would change behaviour.
    for (const spaceNode of spaces!) {
      // if(spaceName.includes("bedroom") || spaceName.includes("bath")){
      const bbc = await this._viewer.model.getNodesBounding([spaceNode]);
      // let ext = bb.extents();
      this.boundingMeasurement(bbc, 1, bbDia * 0.01, bbCenter);
      this.boundingMeasurement(bbc, 2, bbDia * 0.01, bbCenter);
      // }
    }

    // get External elements
    /* let externalNodes = await this.getExternalElements();
    for (const wallnode of externalNodes) {
      let bb = await this._viewer.model.getNodesBounding([wallnode]);
      let ext = bb.extents();
      this.boundingMeasurement(
        bb,
        ext.x > ext.y ? 1 : 2,
        bbDia * 0.05,
        bbCenter
      );
    } */
    // Measure walls
    /* let walls = this.typeIdMap.get("IFCWALLSTANDARDCASE");
    let wall1 = this.typeIdMap.get("IFCWALL");
    if (wall1 && wall1.size) walls.add(...wall1);

    for (const wallnode of walls) {
      let bb = await this._viewer.model.getNodesBounding([wallnode]);
      let ext = bb.extents();
      this.boundingMeasurement(
        bb,
        ext.x > ext.y ? 1 : 2,
        bbDia * 0.05,
        bbCenter
      );
    } */
  }

  boundingMeasurement(bb: Box, axis: number, placementTol = 0.1, refPoint: Point3, side = 0) {
    const markup = new BoundingDistanceMarkup(this._viewer);
    // KNOWN BUG, preserved verbatim: neither is assigned when `axis` is something other than
    // 1 or 2, and the code below dereferences them anyway. Definite-assignment assertions only.
    let l1!: Point3;
    let l2!: Point3;
    if (axis == 1) {
      l1 = new Point3(bb.min.x, side ? bb.min.y : bb.max.y, bb.max.z);
      l2 = new Point3(bb.max.x, side ? bb.min.y : bb.max.y, bb.max.z);
    } else if (axis == 2) {
      l1 = new Point3(side ? bb.min.x : bb.max.x, bb.min.y, bb.max.z);
      l2 = new Point3(side ? bb.min.x : bb.max.x, bb.max.y, bb.max.z);
    }

    let plside = 0;
    const dir = Point3.subtract(l2, l1).normalize();
    if (dir.isAxis()) {
      if (dir.x !== 0.0) plside = l1.y < refPoint.y ? 1 : 0;
      else if (dir.y !== 0.0) plside = l1.x < refPoint.x ? 1 : 0;
    }

    markup.setFirstPointPosition(l1);
    markup.setSecondPointPosition(l2);

    markup._positions[3] = l1.copy();
    markup._positions[4] = l2.copy();

    const pl = plside ? -placementTol : placementTol;
    if (axis == 1) {
      markup._positions[3].y += pl;
      markup._positions[4].y += pl;
    } else if (axis == 2) {
      markup._positions[3].x += pl;
      markup._positions[4].x += pl;
    }

    markup._positions[2] = Point3.add(markup._positions[3], markup._positions[4]).scale(0.5);

    this._viewer.measureManager.addMeasurement(markup);
  }
}
